<script>
  import { Button, TextInput, Dropdown } from "carbon-components-svelte";
  import ChevronLeft from "carbon-icons-svelte/lib/ChevronLeft.svelte";
  import Edit from "carbon-icons-svelte/lib/Edit.svelte";
  import { getTrailingIdFromString, showRouteSchedulePopup } from "../distance";
  import { fetchRouteLoads } from "../../../data";
  import { mapbox, key } from "../../mapbox";
  import { clearYardPriceMarkers, clearTripCalculations } from "./clearMap";
  import YardDeatils from "../../../forms/Yard/YardDeatils.svelte";
  import CompanyForm from "../../../forms/Company/CompanyForm.svelte";

  import {
    routeSources,
    loadAddresCoordinate,
    quoteSelectedMaterial,
    yardPriceMarkers,
    tripCalculations,
    tripCalculationsMarkers,
    yards,
    customerQuote,
    loadAddressText,
    routeTravelEstimates,
    activeRouteSchedule,
  } from "../../../stores";
  import { createEventDispatcher, onDestroy } from "svelte";
  import { reportYardImageError, encodePolyline, getYardName } from "../../../helpers";

  export let map;
  export let disableCalculate = true;
  export let routesCount = 0;

  const dispatch = createEventDispatcher();

  let selectedYard = null;
  let showYardDetails = false;
  let showYardCompany = false;
  let yardCompany = {};

  let tonnes = 0;
  let startTime = "07:00";
  let finishTime = "17:00";
  let loadTime = 10;
  let tipTime = 10;
  let truckCapacityTonne = 19;
  let trailerCapacityTonne = 19;
  let truckDayRate = 1500;

  let hasResults = false;
  let isServerCalculated = false;
  let isCalculatingAll = false;
  let completedCount = 0;
  let totalToCalculate = 0;
  let calculationRunId = 0;
  let yardCalcMarkersMap = {};
  let results = [];
  let cheapestYardName = "";
  let cheapestCost = null;
  let isCheapestTotalCost = false;
  let copiedYardId = null;
  let copiedTimeout = null;



  $: isYardQuoted = (yardId, mat) => {
    const pkgs = $customerQuote.haulagePackages || [];
    const checkMat = (mat && mat !== "All") ? mat : ($quoteSelectedMaterial !== "All" ? $quoteSelectedMaterial : null);
    return pkgs.some((p) => p.yardId === yardId && (!checkMat || p.material === checkMat));
  };

  $: currentMaterialQuotePkg = ($customerQuote.haulagePackages && $customerQuote.haulagePackages.length > 0)
    ? $customerQuote.haulagePackages.find(
        (p) => p.material === ($quoteSelectedMaterial !== "All" ? $quoteSelectedMaterial : p.material)
      )
    : $customerQuote.haulagePackage;

  function copyToQuote(item) {
    copiedYardId = item.yard.id;
    if (copiedTimeout) clearTimeout(copiedTimeout);
    copiedTimeout = setTimeout(() => {
      copiedYardId = null;
    }, 2200);

    let targetMaterial = (item && item.material && item.material !== "All")
      ? item.material
      : (($quoteSelectedMaterial && $quoteSelectedMaterial !== "All")
          ? $quoteSelectedMaterial
          : "");

    // Initialize haulagePackages if needed and seed from existing haulagePackage
    if (!$customerQuote.haulagePackages || !Array.isArray($customerQuote.haulagePackages) || $customerQuote.haulagePackages.length === 0) {
      if ($customerQuote.haulagePackage) {
        if (!$customerQuote.haulagePackage.id) {
          $customerQuote.haulagePackage.id = 'pkg_' + Date.now();
        }
        $customerQuote.haulagePackages = [{ ...$customerQuote.haulagePackage }];
      } else {
        $customerQuote.haulagePackages = [];
      }
    }

    if (!targetMaterial || targetMaterial === "All" || targetMaterial === "General Material") {
      const existingMats = ($customerQuote.haulagePackages || []).map((p) => p.material);
      let num = 1;
      while (existingMats.includes(`Material ${num}`)) {
        num++;
      }
      targetMaterial = `Material ${num}`;
    }

    // Match existing package by BOTH material AND yardId so multiple materials or yards are preserved!
    const existingIndex = $customerQuote.haulagePackages.findIndex(
      (p) => p.material === targetMaterial && p.yardId === item.yard.id
    );

    const prevPkg = existingIndex !== -1 ? $customerQuote.haulagePackages[existingIndex] : null;
    const pkgId = prevPkg?.id || ('pkg_' + Date.now() + '_' + Math.floor(Math.random() * 10000));

    // Resolve route polyline with multi-tier fallback
    let resolvedPolyline = item.load?.polyline || item.polyline || item.yard?.polyline || "";
    let resolvedCoordinates = null;

    if (resolvedPolyline && typeof resolvedPolyline === "object") {
      if (Array.isArray(resolvedPolyline.coordinates)) {
        resolvedCoordinates = resolvedPolyline.coordinates;
        resolvedPolyline = encodePolyline(resolvedCoordinates);
      } else {
        resolvedPolyline = "";
      }
    }

    // Check individual legs in load.routes
    if (!resolvedPolyline && item.load?.routes && Array.isArray(item.load.routes)) {
      for (const r of item.load.routes) {
        if (r.geometry) {
          if (typeof r.geometry === "string") {
            resolvedPolyline = r.geometry;
            break;
          } else if (Array.isArray(r.geometry.coordinates)) {
            resolvedCoordinates = r.geometry.coordinates;
            resolvedPolyline = encodePolyline(resolvedCoordinates);
            break;
          }
        } else if (r.polyline) {
          resolvedPolyline = r.polyline;
          break;
        }
      }
    }

    // Check routeTravelEstimates
    if (!resolvedPolyline && $routeTravelEstimates) {
      const estimate = $routeTravelEstimates[item.yard.id] || $routeTravelEstimates[String(item.yard.id)];
      if (estimate) {
        if (typeof estimate.geometry === "string") {
          resolvedPolyline = estimate.geometry;
        } else if (Array.isArray(estimate.geometry?.coordinates)) {
          resolvedCoordinates = estimate.geometry.coordinates;
          resolvedPolyline = encodePolyline(resolvedCoordinates);
        } else if (Array.isArray(estimate.coordinates)) {
          resolvedCoordinates = estimate.coordinates;
          resolvedPolyline = encodePolyline(resolvedCoordinates);
        }
      }
    }

    // Check Mapbox map line sources
    if (!resolvedPolyline && map && $routeSources) {
      for (const routeSource of ($routeSources || [])) {
        const trailingId = getTrailingIdFromString(routeSource);
        const lineSource = map.getSource("routeSource" + trailingId);
        if (!lineSource) continue;
        const data = lineSource._options?.data || lineSource.options?.data || lineSource._data || lineSource.serialize?.()?.data;
        const yId = data?.yardId ?? data?.properties?.yardId;
        if (yId != null && String(yId) === String(item.yard?.id)) {
          const coords = data?.geometry?.coordinates;
          if (Array.isArray(coords) && coords.length > 0) {
            resolvedCoordinates = coords;
            resolvedPolyline = encodePolyline(coords);
            break;
          }
        }
      }
    }

    // Coordinate resolution for straight-line fallback and directions
    const loadCoords = $loadAddresCoordinate;
    let yardLng = item.yard?.address?.coordinates?.lng ?? item.yard?.address?.coordinates?.longitude ?? item.yard?.coordinates?.lng ?? (Array.isArray(item.yard?.coordinates) ? item.yard.coordinates[0] : null);
    let yardLat = item.yard?.address?.coordinates?.lat ?? item.yard?.address?.coordinates?.latitude ?? item.yard?.coordinates?.lat ?? (Array.isArray(item.yard?.coordinates) ? item.yard.coordinates[1] : null);

    if ((yardLng == null || yardLat == null) && $yards) {
      const matchedYard = $yards.find((y) => String(y.id) === String(item.yard?.id));
      if (matchedYard) {
        yardLng = matchedYard.address?.coordinates?.lng ?? matchedYard.coordinates?.lng;
        yardLat = matchedYard.address?.coordinates?.lat ?? matchedYard.coordinates?.lat;
      }
    }

    if (!resolvedPolyline && loadCoords && Array.isArray(loadCoords) && loadCoords.length >= 2 && yardLng != null && yardLat != null) {
      resolvedCoordinates = [
        [Number(loadCoords[0]), Number(loadCoords[1])],
        [Number(yardLng), Number(yardLat)]
      ];
      resolvedPolyline = encodePolyline(resolvedCoordinates);
    }

    const haulagePackage = {
      id: pkgId,
      yardId: item.yard.id,
      yardName: getYardName(item.yard),
      suburb:
        item.yard.address && item.yard.address.suburb
          ? item.yard.address.suburb
          : item.yard.address
            ? item.yard.address.street
            : "",
      image: item.yard.image,
      material: targetMaterial,
      polyline: resolvedPolyline || null,
      coordinates: resolvedCoordinates || null,
      loadCoordinates: loadCoords || null,
      yardCoordinates: (yardLng != null && yardLat != null) ? { lat: Number(yardLat), lng: Number(yardLng) } : null,
      load: {
        ...(item.load || {}),
        polyline: resolvedPolyline || item.load?.polyline || null,
      },
      tonneRate: item.tonneRate,
      totalCapacity: item.totalCapacity,
      truckCapacityTonne: Number(truckCapacityTonne),
      trailerCapacityTonne: Number(trailerCapacityTonne),
      costPerLoad: item.costPerLoad,
      runs: item.runs,
      tonnesDeliveredInDay: item.tonnesDeliveredInDay,
      totalTonnes: Number(tonnes) || 0,
      totalLoads: item.totalLoads,
      totalDays: item.totalDays,
      billedDays: item.billedDays,
      truckCost: item.truckCost,
      materialCost: item.materialCost,
      finalCostPerTonne: item.finalCostPerTonne,
      dayRate: item.dayRate,
      dailyTolls: item.dailyTolls || 0,
      tollPerLoad: item.tollPerLoad || 0,
      totalTolls: item.totalTolls != null ? item.totalTolls : (item.dailyTolls || 0),
      totalCost: item.totalCost,
      isCheapest: item.isCheapest,
      startTime: startTime,
      finishTime: finishTime,
      loadTime: loadTime,
      tipTime: tipTime,
      isEstimate: !isServerCalculated,
      travelMins: item.travelMins != null
        ? item.travelMins
        : ($routeTravelEstimates && $routeTravelEstimates[item.yard.id]
            ? Math.round($routeTravelEstimates[item.yard.id].durationSeconds / 60)
            : null),
      allocatedTrucks: prevPkg?.allocatedTrucks || 1,
      margins: prevPkg?.margins || {
        tonneRate: { value: 0, mode: "percent" },
        costPerLoad: { value: 0, mode: "percent" },
        dayRate: { value: 0, mode: "percent" },
        cartageCost: { value: 0, mode: "percent" },
        totalTolls: { value: 0, mode: "percent" },
        totalCost: { value: 15, mode: "percent" }
      },
      siteAddress: prevPkg?.siteAddress || $loadAddressText || "",
      load_address: prevPkg?.load_address || {
        street: prevPkg?.siteAddress || $loadAddressText || "",
        coordinates: (loadCoords && Array.isArray(loadCoords) && loadCoords.length >= 2) ? { lat: Number(loadCoords[1]), lng: Number(loadCoords[0]) } : null
      }
    };

    if (existingIndex !== -1) {
      $customerQuote.haulagePackages[existingIndex] = haulagePackage;
    } else {
      $customerQuote.haulagePackages = [...$customerQuote.haulagePackages, haulagePackage];
    }

    $customerQuote.activePackageId = pkgId;
    $customerQuote.activeMaterialTab = targetMaterial;
    $customerQuote.haulagePackage = haulagePackage;
    $customerQuote = $customerQuote;

    // Asynchronously fetch driving route geometry via Mapbox Directions API if coordinates exist
    if (loadCoords && Array.isArray(loadCoords) && loadCoords.length >= 2 && yardLng != null && yardLat != null && typeof MAPBOX_TOKEN !== "undefined" && MAPBOX_TOKEN) {
      const dirUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${loadCoords[0]},${loadCoords[1]};${yardLng},${yardLat}?overview=full&geometries=polyline&access_token=${MAPBOX_TOKEN}`;
      fetch(dirUrl)
        .then((r) => r.json())
        .then((d) => {
          if (d && d.routes && d.routes[0] && d.routes[0].geometry) {
            const drivingPoly = d.routes[0].geometry;
            haulagePackage.polyline = drivingPoly;
            if (haulagePackage.load) {
              haulagePackage.load.polyline = drivingPoly;
            }
            const pIdx = ($customerQuote.haulagePackages || []).findIndex((p) => p.id === haulagePackage.id);
            if (pIdx !== -1) {
              $customerQuote.haulagePackages[pIdx].polyline = drivingPoly;
            }
            if ($customerQuote.haulagePackage && $customerQuote.haulagePackage.id === haulagePackage.id) {
              $customerQuote.haulagePackage.polyline = drivingPoly;
            }
            $customerQuote = $customerQuote;
          }
        })
        .catch((err) => console.warn("[TripCalculate] Background directions fetch:", err));
    }

    dispatch("copy-to-quote", haulagePackage);
  }

  function closeThisWindow() {
    dispatch("close-calculate-window");
  }

  let displayCalculateButton = true;

  function parseTimeToMinutes(timeStr) {
    if (!timeStr) return 0;
    const str = String(timeStr).trim();
    const parts = str.split(":");
    if (parts.length < 2) return 0;
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    return h * 60 + m;
  }

  function getBilledDays(days) {
    if (!days || days <= 0) return 0;
    const wholeDays = Math.floor(days);
    const remainder = days - wholeDays;
    if (remainder < 0.0001) {
      return wholeDays;
    } else if (remainder <= 0.50001) {
      return wholeDays + 0.5;
    } else {
      return wholeDays + 1.0;
    }
  }

  function formatDays(days) {
    if (days === null || days === undefined || isNaN(days)) return "0 days";
    const num = Number(days);
    const formatted = num % 1 === 0 ? num.toString() : num.toFixed(1);
    return `${formatted} ${num === 1 ? "day" : "days"}`;
  }

  function formatCompactCost(val) {
    if (!val || isNaN(val)) return "$0";
    const num = Number(val);
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 10000) return `$${Math.round(num / 1000)}k`;
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}k`;
    return `$${Math.round(num)}`;
  }

  function calculateYardHaulageMetrics({
    tonneRate,
    runs,
    totalCapacity,
    dayRate,
    totalTonnes,
    dailyTolls = 0,
  }) {
    let costPerLoad = null;
    if (tonneRate !== null && totalCapacity > 0) {
      costPerLoad = tonneRate * totalCapacity;
    }

    let tonnesDeliveredInDay = null;
    if (runs !== null && totalCapacity > 0) {
      tonnesDeliveredInDay = runs * totalCapacity;
    }

    let totalLoads = null;
    let totalDays = null;
    let billedDays = null;
    let truckCost = null;
    let materialCost = null;
    let totalCost = null;
    let finalCostPerTonne = null;
    let tollPerLoad = null;
    let totalTolls = null;

    const tonnesNum = Math.max(0, Number(totalTonnes) || 0);
    const tollsPerDay = Math.max(0, Number(dailyTolls) || 0);

    if (runs !== null && runs > 0 && totalCapacity > 0) {
      tollPerLoad = tollsPerDay > 0 ? (tollsPerDay / runs) : 0;
      if (tonnesNum > 0) {
        totalLoads = Math.ceil(tonnesNum / totalCapacity);
        totalDays = totalLoads / runs;
        billedDays = getBilledDays(totalDays);
        truckCost = billedDays * dayRate;
        materialCost = tonneRate !== null ? tonnesNum * tonneRate : 0;
        totalTolls = Math.round(tollPerLoad * totalLoads * 100) / 100;
        if (tonneRate !== null) {
          totalCost = truckCost + materialCost + totalTolls;
          finalCostPerTonne = totalCost / tonnesNum;
        }
      } else {
        totalLoads = 0;
        totalDays = 0;
        billedDays = 0;
        truckCost = 0;
        materialCost = 0;
        totalTolls = tollsPerDay;
        if (tonneRate !== null) {
          totalCost = 0;
          finalCostPerTonne = 0;
        }
      }
    } else if (tonneRate !== null && tonnesNum > 0 && totalCapacity > 0) {
      totalLoads = Math.ceil(tonnesNum / totalCapacity);
      materialCost = tonnesNum * tonneRate;
      totalTolls = tollsPerDay;
    } else {
      totalTolls = tollsPerDay;
    }

    return {
      costPerLoad,
      tonnesDeliveredInDay,
      totalLoads,
      totalDays,
      billedDays,
      truckCost,
      materialCost,
      totalTolls,
      tollPerLoad,
      dailyTolls: tollsPerDay,
      totalCost,
      finalCostPerTonne,
    };
  }

  function compareResultsByTotalCost(a, b) {
    const hasTotA = a.totalCost !== null && a.totalCost !== undefined && a.totalCost > 0;
    const hasTotB = b.totalCost !== null && b.totalCost !== undefined && b.totalCost > 0;

    if (hasTotA && hasTotB) {
      if (a.totalCost !== b.totalCost) {
        return a.totalCost - b.totalCost;
      }
      if (a.costPerLoad !== null && b.costPerLoad !== null && a.costPerLoad !== b.costPerLoad) {
        return a.costPerLoad - b.costPerLoad;
      }
    } else if (hasTotA && !hasTotB) {
      return -1;
    } else if (!hasTotA && hasTotB) {
      return 1;
    }

    // Fallback if neither has a positive totalCost (e.g. 0 tonnes or estimates)
    if (a.costPerLoad !== null && b.costPerLoad !== null) {
      if (a.costPerLoad !== b.costPerLoad) {
        return a.costPerLoad - b.costPerLoad;
      }
    } else if (a.costPerLoad !== null && b.costPerLoad === null) {
      return -1;
    } else if (a.costPerLoad === null && b.costPerLoad !== null) {
      return 1;
    }

    if (a.hasRate && !b.hasRate) return -1;
    if (!a.hasRate && b.hasRate) return 1;

    const nameA = getYardName(a.yard);
    const nameB = getYardName(b.yard);
    return nameA.localeCompare(nameB);
  }

  function getMaterialPrice(yard, selectedMaterial) {
    if (!yard || !yard.accepts) return null;
    let mat = null;
    if (selectedMaterial && selectedMaterial !== "All") {
      mat = yard.accepts.find((m) => m.name === selectedMaterial);
    } else if (yard.accepts.length > 0) {
      mat =
        yard.accepts.find((m) => m.prices && m.prices.length > 0) ||
        yard.accepts[0];
    }
    if (!mat || !mat.prices || mat.prices.length === 0) return null;

    let lowest = null;
    for (const p of mat.prices) {
      const val = parseFloat(p.price);
      if (!isNaN(val) && (lowest === null || val < lowest.val)) {
        lowest = { val, item: p };
      }
    }
    return lowest ? lowest.item : null;
  }

  function extractTollError(itemOrLoad) {
    if (!itemOrLoad) return null;
    if (itemOrLoad.tollError) return String(itemOrLoad.tollError);
    const load = itemOrLoad.load || itemOrLoad;
    if (!load) return null;

    if (load.toll_error) return String(load.toll_error);

    const routes = load.routes;
    if (routes) {
      if (typeof routes === "object" && !Array.isArray(routes)) {
        if (routes.toll_error) return String(routes.toll_error);
      }
      if (Array.isArray(routes)) {
        if (routes.toll_error) return String(routes.toll_error);
        const errors = [];
        for (const r of routes) {
          if (r && r.toll_error) {
            const errStr = String(r.toll_error).trim();
            if (errStr && !errors.includes(errStr)) {
              errors.push(errStr);
            }
          }
        }
        if (errors.length > 0) return errors.join(" • ");
      } else if (typeof routes === "object") {
        const errors = [];
        for (const key of Object.keys(routes)) {
          const val = routes[key];
          if (val && typeof val === "object" && val.toll_error) {
            const errStr = String(val.toll_error).trim();
            if (errStr && !errors.includes(errStr)) {
              errors.push(errStr);
            }
          }
        }
        if (errors.length > 0) return errors.join(" • ");
      }
    }

    if (itemOrLoad.routes) {
      if (itemOrLoad.routes.toll_error) return String(itemOrLoad.routes.toll_error);
      if (Array.isArray(itemOrLoad.routes)) {
        const err = itemOrLoad.routes.find(r => r && r.toll_error)?.toll_error;
        if (err) return String(err);
      }
    }

    return null;
  }

  function computeEstimates() {
    const truckCap = Number(truckCapacityTonne) || 0;
    const trailerCap = Number(trailerCapacityTonne) || 0;
    const totalCapacity = truckCap + trailerCap;
    const dayRate = Number(truckDayRate) || 0;
    const currentMaterial = $quoteSelectedMaterial;

    const startMins = parseTimeToMinutes(startTime);
    const finishMins = parseTimeToMinutes(finishTime);
    let availableMins = finishMins - startMins;
    if (availableMins <= 0) {
      availableMins += 24 * 60;
    }

    const loadMins = Math.max(0, Number(loadTime) || 0);
    const tipMins = Math.max(0, Number(tipTime) || 0);

    const allYards = $yards || [];
    if (allYards.length === 0) return;

    const estimatesMap = $routeTravelEstimates || {};

    const processed = allYards.map((yard) => {
      let tonneRate = null;
      const matPrice = getMaterialPrice(yard, currentMaterial);
      if (
        matPrice &&
        matPrice.price != null &&
        !isNaN(parseFloat(matPrice.price))
      ) {
        tonneRate = parseFloat(matPrice.price);
      }

      const routeEst = estimatesMap[yard.id] ?? estimatesMap[String(yard.id)];
      let runs = null;
      let travelMins = null;

      if (routeEst && routeEst.durationSeconds != null) {
        travelMins = Math.max(1, Math.round(routeEst.durationSeconds / 60));

        // Turnaround calculation formula:
        // 1st load: startTime + loadTime + travelTime + tipTime
        const firstLoadMins = loadMins + travelMins + tipMins;
        if (firstLoadMins <= availableMins) {
          runs = 1;
          let elapsed = firstLoadMins;
          // Consecutive loads: travel back to site + load + travel to yard + tip
          const consecutiveLoadMins =
            travelMins + loadMins + travelMins + tipMins;
          if (consecutiveLoadMins > 0) {
            const additionalRuns = Math.floor(
              (availableMins - elapsed) / consecutiveLoadMins,
            );
            runs += Math.max(0, additionalRuns);
          }
        } else {
          runs = 0;
        }
      }

      const metrics = calculateYardHaulageMetrics({
        tonneRate,
        runs,
        totalCapacity,
        dayRate,
        totalTonnes: tonnes,
      });

      const hasRate = tonneRate !== null;

      return {
        yard,
        load: null,
        material: currentMaterial || "General Material",
        tonneRate,
        runs,
        costPerLoad: metrics.costPerLoad,
        tonnesDeliveredInDay: metrics.tonnesDeliveredInDay,
        totalLoads: metrics.totalLoads,
        totalDays: metrics.totalDays,
        billedDays: metrics.billedDays,
        truckCost: metrics.truckCost,
        materialCost: metrics.materialCost,
        totalCost: metrics.totalCost,
        finalCostPerTonne: metrics.finalCostPerTonne,
        dailyTolls: 0,
        tollPerLoad: 0,
        totalTolls: null,
        hasRate,
        totalCapacity,
        dayRate,
        travelMins,
        isCheapest: false,
        calcState: "estimate",
      };
    });

    // Ordering: cheapest to most expensive by TOTAL COST (falling back to costPerLoad)
    processed.sort(compareResultsByTotalCost);

    const firstPriced = processed.find(
      (p) => (p.totalCost !== null && p.totalCost > 0) || p.costPerLoad !== null
    );
    if (firstPriced) {
      firstPriced.isCheapest = true;
      cheapestYardName = getYardName(firstPriced.yard);
      const hasTotal = firstPriced.totalCost !== null && firstPriced.totalCost > 0;
      cheapestCost = hasTotal ? firstPriced.totalCost : firstPriced.costPerLoad;
      isCheapestTotalCost = hasTotal;
    } else {
      cheapestYardName = "";
      cheapestCost = null;
      isCheapestTotalCost = false;
    }

    results = processed;
    hasResults = true;
  }

  function processResults(loads) {
    const truckCap = Number(truckCapacityTonne) || 0;
    const trailerCap = Number(trailerCapacityTonne) || 0;
    const totalCapacity = truckCap + trailerCap;
    const dayRate = Number(truckDayRate) || 0;
    const currentMaterial = $quoteSelectedMaterial;

    const allYards = $yards || [];
    const processed = allYards.map((yard) => {
      const load = (loads || []).find((l) => l.yard && l.yard.id === yard.id);

      let tonneRate = null;
      const matPrice = getMaterialPrice(yard, currentMaterial);
      if (
        matPrice &&
        matPrice.price != null &&
        !isNaN(parseFloat(matPrice.price))
      ) {
        tonneRate = parseFloat(matPrice.price);
      } else if (
        load &&
        load.costs &&
        load.costs.tip_tonne_price != null &&
        !isNaN(Number(load.costs.tip_tonne_price))
      ) {
        tonneRate = Number(load.costs.tip_tonne_price);
      } else if (
        load &&
        load.costs &&
        load.costs.total_tonne_price != null &&
        !isNaN(Number(load.costs.total_tonne_price))
      ) {
        tonneRate = Number(load.costs.total_tonne_price);
      }

      let runs = null;
      if (load && typeof load.total_loads === "number") {
        runs = load.total_loads;
      }

      let dailyTolls = 0;
      if (load && load.total_tolls != null && !isNaN(Number(load.total_tolls))) {
        dailyTolls = Number(load.total_tolls);
      } else if (load && load.routes && Array.isArray(load.routes)) {
        dailyTolls = load.routes.reduce((acc, r) => acc + (r.toll || 0), 0) / 100;
      }

      const tollError = extractTollError(load);

      const metrics = calculateYardHaulageMetrics({
        tonneRate,
        runs,
        totalCapacity,
        dayRate,
        totalTonnes: tonnes,
        dailyTolls,
      });

      const hasRate = tonneRate !== null;

      return {
        yard,
        load,
        material: currentMaterial || "General Material",
        tonneRate,
        runs,
        dailyTolls: metrics.dailyTolls,
        tollPerLoad: metrics.tollPerLoad,
        totalTolls: metrics.totalTolls,
        tollError,
        costPerLoad: metrics.costPerLoad,
        tonnesDeliveredInDay: metrics.tonnesDeliveredInDay,
        totalLoads: metrics.totalLoads,
        totalDays: metrics.totalDays,
        billedDays: metrics.billedDays,
        truckCost: metrics.truckCost,
        materialCost: metrics.materialCost,
        totalCost: metrics.totalCost,
        finalCostPerTonne: metrics.finalCostPerTonne,
        hasRate,
        totalCapacity,
        dayRate,
        isCheapest: false,
        calcState: load ? "done" : "no-route",
      };
    });

    // Ordering: cheapest to most expensive by TOTAL COST (falling back to costPerLoad)
    processed.sort(compareResultsByTotalCost);

    const firstPriced = processed.find(
      (p) => (p.totalCost !== null && p.totalCost > 0) || p.costPerLoad !== null
    );
    if (firstPriced) {
      firstPriced.isCheapest = true;
      cheapestYardName = getYardName(firstPriced.yard);
      const hasTotal = firstPriced.totalCost !== null && firstPriced.totalCost > 0;
      cheapestCost = hasTotal ? firstPriced.totalCost : firstPriced.costPerLoad;
      isCheapestTotalCost = hasTotal;

      // Ensure cheapest flag across loads matches the lowest total cost
      (loads || []).forEach((l) => {
        if (l.yard) {
          l.cheapest = l.yard.id === firstPriced.yard.id;
        }
      });
      ($yards || []).forEach((y) => {
        if (y.load) {
          y.load.cheapest = y.id === firstPriced.yard.id;
        }
      });
    } else {
      cheapestYardName = "";
      cheapestCost = null;
      isCheapestTotalCost = false;
    }

    // Update map markers and route highlights for all yards
    if (map && isServerCalculated) {
      const yardToTrailing = {};
      ($routeSources || []).forEach((routeSource) => {
        const trailingId = getTrailingIdFromString(routeSource);
        const lineSource = map.getSource("routeSource" + trailingId);
        if (!lineSource) return;
        const data =
          lineSource._options?.data ||
          lineSource.options?.data ||
          lineSource._data;
        const yId = data?.yardId ?? data?.properties?.yardId;
        if (yId != null) {
          yardToTrailing[yId] = trailingId;
        }
      });

      refreshMapCheapestHighlights(firstPriced?.yard?.id, yardToTrailing);
    }

    // Sync attached customer quote package if active
    if ($customerQuote.haulagePackages && $customerQuote.haulagePackages.length > 0) {
      const currentMaterial = ($quoteSelectedMaterial && $quoteSelectedMaterial !== "All")
        ? $quoteSelectedMaterial
        : null;
      if (currentMaterial) {
        const pkgIndex = $customerQuote.haulagePackages.findIndex(
          (p) => p.material === currentMaterial
        );
        if (pkgIndex !== -1) {
          const pkg = $customerQuote.haulagePackages[pkgIndex];
          const quotedItem = processed.find((p) => p.yard.id === pkg.yardId);
          if (quotedItem) {
            pkg.tonneRate = quotedItem.tonneRate;
            pkg.costPerLoad = quotedItem.costPerLoad;
            pkg.totalCost = quotedItem.totalCost;
            pkg.totalTonnes = Number(tonnes) || 0;
            pkg.totalLoads = quotedItem.totalLoads;
            pkg.totalDays = quotedItem.totalDays;
            pkg.billedDays = quotedItem.billedDays;
            pkg.truckCost = quotedItem.truckCost;
            pkg.materialCost = quotedItem.materialCost;
            pkg.finalCostPerTonne = quotedItem.finalCostPerTonne;
            pkg.isCheapest = quotedItem.isCheapest;
            const resPoly = quotedItem.load?.polyline || quotedItem.load?.geometry;
            if (resPoly) {
              pkg.polyline = resPoly;
              if (pkg.load) {
                pkg.load.polyline = resPoly;
              }
            }
            if ($customerQuote.activeMaterialTab === currentMaterial) {
              $customerQuote.haulagePackage = pkg;
            }
          }
        }
      }
    } else if ($customerQuote.haulagePackage) {
      const quotedItem = processed.find(
        (p) => p.yard.id === $customerQuote.haulagePackage.yardId,
      );
      if (quotedItem) {
        $customerQuote.haulagePackage.tonneRate = quotedItem.tonneRate;
        $customerQuote.haulagePackage.costPerLoad = quotedItem.costPerLoad;
        $customerQuote.haulagePackage.totalCost = quotedItem.totalCost;
        $customerQuote.haulagePackage.totalTonnes = Number(tonnes) || 0;
        $customerQuote.haulagePackage.totalLoads = quotedItem.totalLoads;
        $customerQuote.haulagePackage.totalDays = quotedItem.totalDays;
        $customerQuote.haulagePackage.billedDays = quotedItem.billedDays;
        $customerQuote.haulagePackage.truckCost = quotedItem.truckCost;
        $customerQuote.haulagePackage.materialCost = quotedItem.materialCost;
        $customerQuote.haulagePackage.finalCostPerTonne =
          quotedItem.finalCostPerTonne;
        $customerQuote.haulagePackage.isCheapest = quotedItem.isCheapest;
        const resPoly = quotedItem.load?.polyline || quotedItem.load?.geometry;
        if (resPoly) {
          $customerQuote.haulagePackage.polyline = resPoly;
        }
      }
    }

    results = processed;
    hasResults = true;
  }

  // Automatically compute haulage estimate as routes become available or parameters change
  $: if (
    !disableCalculate &&
    !isServerCalculated &&
    $yards &&
    $yards.length > 0 &&
    Object.keys($routeTravelEstimates || {}).length > 0
  ) {
    $routeTravelEstimates,
      $quoteSelectedMaterial,
      truckCapacityTonne,
      trailerCapacityTonne,
      truckDayRate,
      startTime,
      finishTime,
      loadTime,
      tipTime,
      tonnes;
    computeEstimates();
  }

  // Reset results if calculation becomes disabled (e.g. routes cleared)
  $: if (disableCalculate) {
    calculationRunId++;
    isCalculatingAll = false;
    hasResults = false;
    results = [];
    cheapestYardName = "";
    cheapestCost = null;
    isCheapestTotalCost = false;
    isServerCalculated = false;
  }

  // Recalculate costs dynamically if fleet specs, selected material, or yard data change while server-calculated results are displayed
  $: if (
    hasResults &&
    isServerCalculated &&
    !isCalculatingAll &&
    $tripCalculations &&
    $tripCalculations.loads
  ) {
    $yards,
      $quoteSelectedMaterial,
      truckCapacityTonne,
      trailerCapacityTonne,
      truckDayRate,
      tonnes;
    processResults($tripCalculations.loads);
  }

  function onRowDoubleClick(item) {
    if (!item || !item.yard) return;
    selectedYard = item.yard;
    showYardDetails = true;
  }

  function onYardDetailsClose() {
    showYardDetails = false;
  }

  function setShowYardCompany(e) {
    yardCompany = e.detail;
    showYardCompany = true;
  }

  function saveCompanyDetails() {
    if (selectedYard) {
      selectedYard.company = yardCompany;
      $yards = ($yards || []).map((y) =>
        y.id === selectedYard.id ? { ...y, company: yardCompany } : y,
      );
      if (results) {
        const item = results.find((r) => r.yard.id === selectedYard.id);
        if (item) {
          item.yard.company = yardCompany;
          results = [...results];
        }
      }
    }
    showYardCompany = false;
  }

  function handleYardSaved(updatedYard) {
    if (!updatedYard || !updatedYard.id) return;

    // 1. Update the yard in $yards store
    $yards = ($yards || []).map((y) =>
      y.id === updatedYard.id ? { ...y, ...updatedYard } : y,
    );

    // 2. If in preliminary estimates mode, recompute estimates
    if (
      !isServerCalculated &&
      Object.keys($routeTravelEstimates || {}).length > 0
    ) {
      computeEstimates();
      return;
    }

    // 3. If in server-calculated mode, reprocess results
    if (isServerCalculated && $tripCalculations && $tripCalculations.loads) {
      processResults($tripCalculations.loads);
    }
  }

  function sortFinalResults() {
    results.sort(compareResultsByTotalCost);

    const firstPriced = results.find(
      (p) => (p.totalCost !== null && p.totalCost > 0) || p.costPerLoad !== null
    );
    if (firstPriced) {
      results.forEach((r) => {
        r.isCheapest = r.yard.id === firstPriced.yard.id;
      });
      cheapestYardName = getYardName(firstPriced.yard);
      const hasTotal = firstPriced.totalCost !== null && firstPriced.totalCost > 0;
      cheapestCost = hasTotal ? firstPriced.totalCost : firstPriced.costPerLoad;
      isCheapestTotalCost = hasTotal;

      ($yards || []).forEach((y) => {
        if (y.load) {
          y.load.cheapest = y.id === firstPriced.yard.id;
        }
      });
      if ($tripCalculations && $tripCalculations.loads) {
        ($tripCalculations.loads || []).forEach((l) => {
          if (l.yard) {
            l.cheapest = l.yard.id === firstPriced.yard.id;
          }
        });
      }
    }
    results = [...results];
  }

  function openScheduleForYard(yardId, forceModal = false) {
    if (!yardId) return;
    const yard = ($yards || []).find((y) => y.id === yardId);
    const item = (results || []).find((r) => r.yard?.id === yardId);
    const calcLoad =
      item?.load ||
      yard?.load ||
      ($tripCalculations?.loads || []).find((l) => l.yard?.id === yardId);

    const routes = calcLoad?.routes || item?.load?.routes || yard?.load?.routes;
    const currentMaterial = ($quoteSelectedMaterial && $quoteSelectedMaterial !== "All")
      ? $quoteSelectedMaterial
      : (item?.material || "");

    if (forceModal) {
      $activeRouteSchedule = {
        yard: yard || item?.yard || calcLoad?.yard,
        load: calcLoad,
        routes: routes || [],
        material: currentMaterial,
        tonnes: Number(tonnes) || 0,
        tonneRate: item?.tonneRate,
      };
      return;
    }

    if (map && yard?.address?.coordinates) {
      showRouteSchedulePopup(
        map,
        [yard.address.coordinates.lng, yard.address.coordinates.lat],
        yard,
        calcLoad,
        routes,
      );
    } else {
      $activeRouteSchedule = {
        yard: yard || item?.yard || calcLoad?.yard,
        load: calcLoad,
        routes: routes || [],
        material: currentMaterial,
        tonnes: Number(tonnes) || 0,
        tonneRate: item?.tonneRate,
      };
    }
  }

  if (typeof window !== "undefined") {
    window.__openRouteScheduleModal = (yardId) => {
      openScheduleForYard(yardId, true);
    };
  }

  onDestroy(() => {
    if (typeof window !== "undefined") {
      delete window.__openRouteScheduleModal;
    }
  });

  function refreshMapCheapestHighlights(cheapestYardId, yardToTrailing) {
    if (!map) return;
    for (const [yIdStr, trailingId] of Object.entries(yardToTrailing)) {
      const yId = Number(yIdStr);
      const item = results.find((r) => r.yard.id === yId);

      // If this yard is still actively calculating, keep its amber styling
      if (item && item.calcState === "calculating") {
        if (map.getLayer("routeLine" + trailingId)) {
          map.setPaintProperty(
            "routeLine" + trailingId,
            "line-color",
            "#f59e0b",
          );
          map.setPaintProperty("routeLine" + trailingId, "line-width", 5);
        }
        continue;
      }

      // Only completed yards get the cheapest highlight (emerald green) or standard blue
      if (item && item.calcState === "done") {
        const isCheapest = yId === cheapestYardId;

        if (map.getLayer("routeLine" + trailingId)) {
          map.setPaintProperty(
            "routeLine" + trailingId,
            "line-color",
            isCheapest ? "#10b981" : "#3b82f6",
          );
          map.setPaintProperty(
            "routeLine" + trailingId,
            "line-width",
            isCheapest ? 5 : 4,
          );
        }
      }
    }
  }

  async function calculateSingleYard(
    currentYardId,
    yardToTrailing,
    baseRequestData,
    currentRunId,
    accumulatedLoads,
  ) {
    if (currentRunId !== calculationRunId) return;

    const trailingId = yardToTrailing[currentYardId];
    const item = results.find((r) => r.yard.id === currentYardId);
    if (!item) return;

    const truckCap = Number(truckCapacityTonne) || 0;
    const trailerCap = Number(trailerCapacityTonne) || 0;
    const totalCapacity = truckCap + trailerCap;
    const dayRate = Number(truckDayRate) || 0;
    const currentMaterial = $quoteSelectedMaterial;

    // Transition this row to calculating state
    item.calcState = "calculating";
    results = [...results];

    // Highlight route line in active amber while calculating
    if (map && trailingId != null && map.getLayer("routeLine" + trailingId)) {
      map.setPaintProperty("routeLine" + trailingId, "line-color", "#f59e0b");
      map.setPaintProperty("routeLine" + trailingId, "line-width", 5);
    }

    try {
      const effectiveMaterial =
        currentMaterial && currentMaterial !== "All"
          ? currentMaterial
          : item.yard?.accepts?.[0]?.name || "Concrete";

      const singleYardPayload = {
        ...baseRequestData,
        material: effectiveMaterial,
        dispose: {
          yards: [{ yard_id: currentYardId }],
        },
      };

      const response = await fetchRouteLoads(singleYardPayload);

      if (currentRunId !== calculationRunId) return;

      if (response && response.loads && response.loads.length > 0) {
        const load = response.loads[0];
        accumulatedLoads.push(load);

        let tonneRate = null;
        if (
          load.costs &&
          load.costs.tip_tonne_price != null &&
          !isNaN(Number(load.costs.tip_tonne_price))
        ) {
          tonneRate = Number(load.costs.tip_tonne_price);
        } else if (
          load.costs &&
          load.costs.total_tonne_price != null &&
          !isNaN(Number(load.costs.total_tonne_price))
        ) {
          tonneRate = Number(load.costs.total_tonne_price);
        } else {
          const matPrice = getMaterialPrice(item.yard, currentMaterial);
          if (
            matPrice &&
            matPrice.price != null &&
            !isNaN(parseFloat(matPrice.price))
          ) {
            tonneRate = parseFloat(matPrice.price);
          }
        }

        let runs = null;
        if (typeof load.total_loads === "number") {
          runs = load.total_loads;
        }

        let dailyTolls = 0;
        if (load.total_tolls != null && !isNaN(Number(load.total_tolls))) {
          dailyTolls = Number(load.total_tolls);
        } else if (load.routes && Array.isArray(load.routes)) {
          dailyTolls = load.routes.reduce((acc, r) => acc + (r.toll || 0), 0) / 100;
        }

        const tollError = extractTollError(load);

        const metrics = calculateYardHaulageMetrics({
          tonneRate,
          runs,
          totalCapacity,
          dayRate,
          totalTonnes: tonnes,
          dailyTolls,
        });

        item.material = currentMaterial;
        item.load = load;
        item.tonneRate = tonneRate;
        item.runs = runs;
        item.dailyTolls = metrics.dailyTolls;
        item.tollPerLoad = metrics.tollPerLoad;
        item.totalTolls = metrics.totalTolls;
        item.tollError = tollError;
        item.costPerLoad = metrics.costPerLoad;
        item.tonnesDeliveredInDay = metrics.tonnesDeliveredInDay;
        item.totalLoads = metrics.totalLoads;
        item.totalDays = metrics.totalDays;
        item.billedDays = metrics.billedDays;
        item.truckCost = metrics.truckCost;
        item.materialCost = metrics.materialCost;
        item.totalCost = metrics.totalCost;
        item.finalCostPerTonne = metrics.finalCostPerTonne;
        item.hasRate = tonneRate !== null;
        item.totalCapacity = totalCapacity;
        item.dayRate = dayRate;
        item.calcState = "done";

        // Re-evaluate cheapest yard among all finished yards so far comparing total cost
        const finished = results.filter((r) => r.calcState === "done");
        finished.sort(compareResultsByTotalCost);
        const bestItem = finished.find(
          (r) => (r.totalCost !== null && r.totalCost > 0) || r.costPerLoad !== null
        );
        const bestYardId = bestItem?.yard?.id || null;

        results.forEach((r) => {
          r.isCheapest = Boolean(bestYardId && r.yard?.id === bestYardId);
        });

        if (bestItem) {
          cheapestYardName = bestItem.yard ? getYardName(bestItem.yard) : "Yard";
          const hasTotal = bestItem.totalCost !== null && bestItem.totalCost > 0;
          cheapestCost = hasTotal ? bestItem.totalCost : bestItem.costPerLoad;
          isCheapestTotalCost = hasTotal;
        }

        // Cache calculated load directly on yard object for instant access by route duration markers
        $yards = ($yards || []).map((y) =>
          y.id === currentYardId ? { ...y, load } : y,
        );
        refreshMapCheapestHighlights(bestYardId, yardToTrailing);

        // Sync calculated load and route geometry to customerQuote haulage packages if already copied
        if ($customerQuote.haulagePackages && $customerQuote.haulagePackages.length > 0) {
          let updatedAny = false;
          $customerQuote.haulagePackages = $customerQuote.haulagePackages.map((pkg) => {
            if (String(pkg.yardId) === String(currentYardId) && (!currentMaterial || currentMaterial === "All" || pkg.material === currentMaterial)) {
              updatedAny = true;
              const poly = load.polyline || load.geometry || pkg.polyline || null;
              return {
                ...pkg,
                load: load,
                polyline: poly,
                tonneRate: tonneRate,
                runs: runs,
                dailyTolls: metrics.dailyTolls,
                tollPerLoad: metrics.tollPerLoad,
                totalTolls: metrics.totalTolls,
                costPerLoad: metrics.costPerLoad,
                tonnesDeliveredInDay: metrics.tonnesDeliveredInDay,
                totalLoads: metrics.totalLoads,
                totalDays: metrics.totalDays,
                billedDays: metrics.billedDays,
                truckCost: metrics.truckCost,
                materialCost: metrics.materialCost,
                totalCost: metrics.totalCost,
                finalCostPerTonne: metrics.finalCostPerTonne,
                isCheapest: Boolean(bestYardId && currentYardId === bestYardId)
              };
            }
            return pkg;
          });
          if (updatedAny) {
            if ($customerQuote.haulagePackage && String($customerQuote.haulagePackage.yardId) === String(currentYardId)) {
              $customerQuote.haulagePackage = $customerQuote.haulagePackages.find((p) => String(p.yardId) === String(currentYardId)) || $customerQuote.haulagePackage;
            }
            $customerQuote = $customerQuote;
          }
        }

        $tripCalculations = { loads: [...accumulatedLoads] };
      } else {
        item.calcState = "error";
        if (
          map &&
          trailingId != null &&
          map.getLayer("routeLine" + trailingId)
        ) {
          map.setPaintProperty(
            "routeLine" + trailingId,
            "line-color",
            "#3b82f6",
          );
          map.setPaintProperty("routeLine" + trailingId, "line-width", 4);
        }
      }
    } catch (err) {
      console.error(
        `Failed to calculate haulage for yard ${currentYardId}:`,
        err,
      );
      item.calcState = "error";
      if (map && trailingId != null && map.getLayer("routeLine" + trailingId)) {
        map.setPaintProperty("routeLine" + trailingId, "line-color", "#3b82f6");
        map.setPaintProperty("routeLine" + trailingId, "line-width", 4);
      }
    }

    completedCount++;
    results = [...results];
  }

  async function calculateHaulage() {
    calculationRunId++;
    const currentRunId = calculationRunId;

    const truckCap = Number(truckCapacityTonne) || 0;
    const trailerCap = Number(trailerCapacityTonne) || 0;
    const totalCapacity = truckCap + trailerCap;
    const dayRate = Number(truckDayRate) || 0;
    const currentMaterial = $quoteSelectedMaterial;

    // Map route lines to yard ids
    const yardToTrailing = {};
    const yardsWithRoutes = [];
    const yardsToCalculate = [];

    const allYards = $yards || [];
    const yardMap = {};
    allYards.forEach((y) => {
      yardMap[y.id] = y;
    });

    ($routeSources || []).forEach((routeSource) => {
      const trailingId = getTrailingIdFromString(routeSource);
      const lineSource = map.getSource("routeSource" + trailingId);
      if (!lineSource) return;
      const data =
        lineSource._options?.data ||
        lineSource.options?.data ||
        lineSource._data;
      const yId = data?.yardId ?? data?.properties?.yardId;
      if (yId != null) {
        yardToTrailing[yId] = trailingId;
        if (!yardsWithRoutes.includes(yId)) {
          yardsWithRoutes.push(yId);
        }
        const yardObj = yardMap[yId];
        const matPrice = getMaterialPrice(yardObj, currentMaterial);
        const hasRate =
          matPrice &&
          matPrice.price != null &&
          !isNaN(parseFloat(matPrice.price));
        if (hasRate && !yardsToCalculate.includes(yId)) {
          yardsToCalculate.push(yId);
        }
      }
    });

    if (yardsWithRoutes.length === 0) {
      alert(
        "No routes found on map to calculate haulage. Please ensure routes are mapped first.",
      );
      return;
    }

    if (yardsToCalculate.length === 0) {
      alert(
        "None of the mapped yards have rates for the selected material (" +
          (currentMaterial || "Disposal") +
          "). Please add material rates to calculate haulage.",
      );
      return;
    }

    // Remove previous price & calculation markers
    for (var i = $yardPriceMarkers.length - 1; i >= 0; i--) {
      if ($yardPriceMarkers[i]?.marker?.remove) {
        $yardPriceMarkers[i].marker.remove();
      }
    }
    $yardPriceMarkers = [];

    ($tripCalculationsMarkers || []).forEach((m) => {
      if (m && m.remove) m.remove();
    });
    $tripCalculationsMarkers = [];
    yardCalcMarkersMap = {};
    if (typeof document !== "undefined") {
      document.querySelectorAll(".trip-calc-marker").forEach((el) => el.remove());
    }

    isServerCalculated = true;
    hasResults = true;
    isCalculatingAll = true;
    completedCount = 0;
    totalToCalculate = yardsToCalculate.length;

    // Populate results list if not present, and initialize calcState
    if (!results || results.length === 0) {
      results = allYards.map((yard) => {
        const hasRoute = yardsWithRoutes.includes(yard.id);
        const willCalculate = yardsToCalculate.includes(yard.id);
        return {
          yard,
          material: currentMaterial,
          load: null,
          tonneRate: null,
          runs: null,
          costPerLoad: null,
          tonnesDeliveredInDay: null,
          totalLoads: null,
          totalDays: null,
          billedDays: null,
          truckCost: null,
          materialCost: null,
          totalCost: null,
          finalCostPerTonne: null,
          hasRate: false,
          totalCapacity,
          dayRate,
          isCheapest: false,
          calcState: willCalculate
            ? "waiting"
            : hasRoute
              ? "done"
              : "no-route",
        };
      });
    } else {
      results.forEach((item) => {
        item.material = currentMaterial;
        if (yardsToCalculate.includes(item.yard.id)) {
          item.calcState = "waiting";
          item.isCheapest = false;
        } else if (yardsWithRoutes.includes(item.yard.id)) {
          item.calcState = "done";
          item.isCheapest = false;
          item.hasRate = false;
          item.tonneRate = null;
          item.costPerLoad = null;
          item.totalCost = null;
          item.finalCostPerTonne = null;
        } else {
          item.calcState = "no-route";
          item.isCheapest = false;
        }
      });
    }

    cheapestYardName = "";
    cheapestCost = null;
    isCheapestTotalCost = false;
    results = [...results];

    function formatTime24(t) {
      if (!t) return "07:00";
      const parts = String(t).trim().split(":");
      if (parts.length === 2) {
        return parts[0].padStart(2, "0") + ":" + parts[1].padStart(2, "0");
      }
      return String(t).trim();
    }

    const baseRequestData = {
      load_coords: {
        longitude: $loadAddresCoordinate[0],
        latitude: $loadAddresCoordinate[1],
      },
      start_time: formatTime24(startTime),
      finish_time: formatTime24(finishTime),
      load_time: Number(loadTime),
      tip_time: Number(tipTime),
      truck_load_capacity: totalCapacity,
      total_material_tonne: Number(tonnes),
      truck_day_rate: dayRate,
      tolls_included: false,
      material: currentMaterial,
    };

    const orderedYardIds = [];
    results.forEach((r) => {
      if (yardsToCalculate.includes(r.yard.id)) {
        orderedYardIds.push(r.yard.id);
      }
    });

    const accumulatedLoads = [];
    const calculationPromises = [];

    // Launch API calls simultaneously, staggered by 1.5 seconds apart
    for (let idx = 0; idx < orderedYardIds.length; idx++) {
      if (currentRunId !== calculationRunId) return;

      const currentYardId = orderedYardIds[idx];
      // Launch asynchronously (does not block launching next yards)
      const p = calculateSingleYard(
        currentYardId,
        yardToTrailing,
        baseRequestData,
        currentRunId,
        accumulatedLoads,
      );
      calculationPromises.push(p);

      // Wait 1.5 seconds before launching the next yard's calculation
      if (idx < orderedYardIds.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        if (currentRunId !== calculationRunId) return;
      }
    }

    // Wait for all concurrent yard calculations to finish
    await Promise.all(calculationPromises);

    if (currentRunId === calculationRunId) {
      isCalculatingAll = false;
      sortFinalResults();
      dispatch("trip-calculated");
    }
  }

  function onTruckTypeSelect(e) {
    truckCapacityTonne = e.detail.selectedItem.value;
  }

  function onTralierSelect(e) {
    trailerCapacityTonne = e.detail.selectedItem.value;
  }

  function clear() {
    calculationRunId++;
    isCalculatingAll = false;
    clearTripCalculations();
    clearYardPriceMarkers();
    yardCalcMarkersMap = {};
    hasResults = false;
    results = [];
    cheapestYardName = "";
    cheapestCost = null;
    isCheapestTotalCost = false;
    isServerCalculated = false;
  }

  function focusYard(item) {
    if (item.yard && item.yard.address && item.yard.address.coordinates) {
      const coords = item.yard.address.coordinates;
      map.flyTo({
        center: [coords.lng, coords.lat],
        zoom: 13.5,
        essential: true,
      });
      const marker = document.getElementById("yard-marker-" + item.yard.id);
      if (marker) {
        marker.classList.add("marker-hovered");
        setTimeout(() => marker.classList.remove("marker-hovered"), 2000);
      }
    }
  }

  function onRowMouseEnter(item) {
    const marker = document.getElementById("yard-marker-" + item.yard.id);
    if (marker) marker.classList.add("marker-hovered");
  }

  function onRowMouseLeave(item) {
    const marker = document.getElementById("yard-marker-" + item.yard.id);
    if (marker) marker.classList.remove("marker-hovered");
  }
</script>

<div class="calc-window {hasResults ? 'has-results' : ''}">
  <div class="card-header">
    <div class="header-info">
      <div class="header-title-row">
        <h2 class="card-title">Haulage & Fleet Calculator</h2>
        {#if disableCalculate}
          <span class="tt-badge tt-badge-slate">Awaiting Routes</span>
        {:else if isCalculatingAll}
          <span class="tt-badge tt-badge-calculating">
            <span class="spinner-wheel small"></span>
            Calculating ({completedCount}/{totalToCalculate})
          </span>
        {:else if hasResults}
          {#if isServerCalculated}
            <span class="tt-badge tt-badge-success"
              >Calculated ({results.length} Yards)</span
            >
          {:else}
            <span class="tt-badge tt-badge-estimate"
              >Estimate ({results.length} Yards)</span
            >
          {/if}
        {:else}
          <span class="tt-badge tt-badge-success">Ready</span>
        {/if}
      </div>
      <span class="card-subtitle"
        >Configure operating parameters and evaluate haulage costs</span
      >
    </div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <button
      class="close-card-btn"
      on:click={closeThisWindow}
      title="Close calculator"
      type="button"
    >
      <ChevronLeft size={20} />
    </button>
  </div>

  <div class="card-body">
    {#if disableCalculate}
      <div class="calc-banner notice">
        <span class="banner-icon">ℹ️</span>
        <div class="banner-content">
          <strong>Mapping required:</strong> Please map routes in the Quote Planner
          first. Once all driving routes appear on the map, this calculator will
          become active.
        </div>
      </div>
    {:else}
      <div class="calc-banner success">
        <span class="banner-icon">✓</span>
        <div class="banner-content">
          {#if isServerCalculated}
            <strong>Haulage fully calculated:</strong> Analyzed schedules and toll
            rates. Click any yard row to inspect routes on the map.
          {:else}
            <strong>Routes mapped:</strong>
            {routesCount > 0 ? `(${routesCount} routes ready) ` : ""}Showing
            <strong>preliminary haulage estimates</strong>
            based on travel times. Click <strong>Calculate Haulage</strong> below
            to analyze full route schedules and tolls.
          {/if}
        </div>
      </div>
    {/if}



    <div class="calc-section-title">Operating Hours & Durations</div>
    <div class="calc-grid-5">
      <div class="calc-field">
        <TextInput
          light
          size="sm"
          disabled={disableCalculate}
          labelText="Total Tonnes"
          bind:value={tonnes}
          placeholder="0"
        />
      </div>
      <div class="calc-field">
        <TextInput
          light
          size="sm"
          disabled={disableCalculate}
          labelText="Start (24h)"
          bind:value={startTime}
          placeholder="07:00"
        />
      </div>
      <div class="calc-field">
        <TextInput
          light
          size="sm"
          disabled={disableCalculate}
          labelText="Finish (24h)"
          bind:value={finishTime}
          placeholder="17:00"
        />
      </div>
      <div class="calc-field">
        <TextInput
          light
          size="sm"
          disabled={disableCalculate}
          labelText="Load (mins)"
          bind:value={loadTime}
          placeholder="10"
        />
      </div>
      <div class="calc-field">
        <TextInput
          light
          size="sm"
          disabled={disableCalculate}
          labelText="Tip (mins)"
          bind:value={tipTime}
          placeholder="10"
        />
      </div>
    </div>

    <div class="calc-section-title">Fleet Specifications & Rates</div>
    <div class="calc-fleet-grid">
      <div class="calc-field">
        <Dropdown
          disabled={disableCalculate}
          light
          size="sm"
          titleText="Truck Type"
          selectedId="2"
          items={[
            { id: "0", text: "6 wheeler - 12t", value: 12 },
            { id: "1", text: "8 wheeler - 15t", value: 15 },
            { id: "2", text: "10 wheeler - 19t", value: 19 },
          ]}
          on:select={(e) => onTruckTypeSelect(e)}
        />
      </div>
      <div class="calc-field">
        <TextInput
          light
          size="sm"
          disabled={disableCalculate}
          labelText="Truck (t)"
          bind:value={truckCapacityTonne}
        />
      </div>
      <div class="calc-field">
        <Dropdown
          disabled={disableCalculate}
          light
          size="sm"
          titleText="Trailer"
          selectedId="3"
          items={[
            { id: "0", text: "None", value: 0 },
            { id: "1", text: "4 wheeler - 12t", value: 12 },
            { id: "2", text: "6 wheeler - 15t", value: 15 },
            { id: "3", text: "8 wheeler - 20t", value: 19 },
          ]}
          on:select={(e) => onTralierSelect(e)}
        />
      </div>
      <div class="calc-field">
        <TextInput
          light
          size="sm"
          disabled={disableCalculate}
          labelText="Trailer (t)"
          bind:value={trailerCapacityTonne}
        />
      </div>
      <div class="calc-field">
        <TextInput
          light
          size="sm"
          disabled={disableCalculate}
          labelText="Day Rate ($)"
          bind:value={truckDayRate}
        />
      </div>
    </div>

    <div class="calc-actions">
      <button
        class="back-planner-btn"
        on:click={() => dispatch("open-quote-window")}
        type="button"
      >
        <span>← Back to Quote Planner</span>
      </button>

      {#if displayCalculateButton}
        <Button
          disabled={disableCalculate || isCalculatingAll}
          on:click={() => calculateHaulage()}
          size="field"
          kind="primary"
        >
          {#if isCalculatingAll}
            <span class="btn-spinner"></span>
            Calculating ({completedCount}/{totalToCalculate})...
          {:else}
            Calculate Haulage
          {/if}
        </Button>
      {/if}
    </div>

    {#if hasResults}
      <div class="results-expansion-panel">
        <div class="results-header-bar">
          <div class="results-title-group">
            <div class="results-title-row">
              <span class="results-main-title">Haulage Comparison Results</span>
              {#if isCalculatingAll}
                <span class="results-calc-chip">
                  <span class="spinner-wheel small"></span> Calculating ({completedCount}/{totalToCalculate})
                </span>
              {:else if !isServerCalculated}
                <span class="results-estimate-chip">Estimate</span>
              {/if}
              <span class="results-count-pill">{results.length} yards</span>
            </div>
            <span class="results-formula-hint">
              Cost/load = rate × {Number(truckCapacityTonne) +
                Number(trailerCapacityTonne)}t &nbsp;•&nbsp; Total = truck cost
              (${truckDayRate}/day) + (rate × {Number(tonnes) || 0}t) + tolls
            </span>
          </div>
          {#if cheapestCost !== null}
            <div class="cheapest-summary-badge">
              <span class="summary-star">★</span>
              <span class="summary-text"
                >Cheapest: <strong>{cheapestYardName}</strong>
                (${cheapestCost.toLocaleString("en-AU", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{isCheapestTotalCost ? " total" : "/load"})</span
              >
            </div>
          {/if}
        </div>

        <div class="results-table-container">
          <div class="results-table-head">
            <div class="th-cell th-yard">Disposal Yard</div>
            <div class="th-cell th-cost-load">Cost / Load</div>
            <div class="th-cell th-runs">Runs / Day</div>
            <div class="th-cell th-loads-days">Loads / Days</div>
            <div class="th-cell th-tolls">Tolls</div>
            <div class="th-cell th-total">Total Cost</div>
            <div class="th-cell th-cost-tonne">Cost / Tonne</div>
            <div class="th-cell th-quote-action">Quote</div>
          </div>

          <div class="results-table-body">
            {#each results as item (item.yard.id)}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div
                class="result-row {item.isCheapest
                  ? 'cheapest-row'
                  : ''} {item.calcState === 'calculating'
                  ? 'is-calculating'
                  : ''} {!item.hasRate && item.calcState === 'done'
                  ? 'unpriced-row'
                  : ''}"
                on:click={() => focusYard(item)}
                on:dblclick={() => onRowDoubleClick(item)}
                on:mouseenter={() => onRowMouseEnter(item)}
                on:mouseleave={() => onRowMouseLeave(item)}
                title="Click to view yard on map • Double-click to view and edit yard details"
              >
                <!-- Yard Column -->
                <div class="td-cell td-yard">
                  <div class="yard-thumb-wrapper">
                    {#if item.yard.image}
                      <img
                        class="yard-thumb"
                        src={"./assests/yards/" + item.yard.image}
                        alt=""
                        on:error={(e) => {
                          reportYardImageError(
                            item.yard,
                            "not_found",
                            "./assests/yards/" + item.yard.image,
                          );
                          e.currentTarget.style.display = "none";
                          const fb =
                            e.currentTarget.parentElement.querySelector(
                              ".placeholder-avatar",
                            );
                          if (fb) fb.style.display = "flex";
                        }}
                      />
                      <div
                        class="yard-thumb placeholder-avatar"
                        style="display: none;"
                      >
                        🏭
                      </div>
                    {:else}
                      <div class="yard-thumb placeholder-avatar">🏭</div>
                    {/if}
                  </div>
                  <div class="yard-info-col">
                    <div class="yard-name-row">
                      <span class="yard-name-text">
                        {getYardName(item.yard)}
                      </span>
                      <button
                        class="yard-row-edit-btn"
                        on:click|stopPropagation={() => onRowDoubleClick(item)}
                        on:dblclick|stopPropagation={() => {}}
                        title="View & edit yard details and material prices"
                        type="button"
                      >
                        <Edit size={12} />
                      </button>
                      {#if item.calcState === "calculating"}
                        <span class="calc-status-badge badge-calculating">
                          <span class="spinner-wheel small"></span>
                          <span>Calculating</span>
                        </span>
                      {:else if item.calcState === "waiting"}
                        <span class="calc-status-badge badge-waiting">
                          <span class="triple-dot-pulse small">
                            <span class="dot dot-1">•</span>
                            <span class="dot dot-2">•</span>
                            <span class="dot dot-3">•</span>
                          </span>
                          <span>Waiting</span>
                        </span>
                      {:else if item.calcState === "error"}
                        <span class="calc-status-badge badge-error">Failed</span
                        >
                      {:else if item.calcState === "no-route"}
                        <span class="calc-status-badge badge-no-route"
                          >No route</span
                        >
                      {:else if item.isCheapest}
                        <span class="best-badge">★ Cheapest</span>
                      {/if}
                    </div>
                    <span class="yard-suburb-text">
                      {item.yard.address && item.yard.address.suburb
                        ? item.yard.address.suburb
                        : item.yard.address
                          ? item.yard.address.street
                          : ""}
                    </span>
                  </div>
                </div>

                <!-- Cost per Load Column -->
                <div class="td-cell td-cost-load">
                  {#if item.calcState === "waiting"}
                    <div
                      class="cell-status-wrapper"
                      title="Queued to calculate"
                    >
                      <span class="triple-dot-pulse">
                        <span class="dot dot-1">•</span>
                        <span class="dot dot-2">•</span>
                        <span class="dot dot-3">•</span>
                      </span>
                    </div>
                  {:else if item.calcState === "calculating"}
                    <div
                      class="cell-status-wrapper is-active-calc"
                      title="Calculating route and haulage costs"
                    >
                      <span class="spinner-wheel"></span>
                      <span class="calc-inline-label">Calculating...</span>
                    </div>
                  {:else if item.calcState === "error"}
                    <span class="no-rate-pill error-pill">Error</span>
                  {:else if item.calcState === "no-route"}
                    <span class="muted-dash">—</span>
                  {:else if item.costPerLoad !== null}
                    <div class="cost-load-value">
                      ${item.costPerLoad.toLocaleString("en-AU", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <div class="calc-sub-formula">
                      ${item.tonneRate}/t × {item.totalCapacity}t
                    </div>
                  {:else}
                    <span class="no-rate-pill">No rate</span>
                  {/if}
                </div>

                <!-- Runs Column -->
                <div class="td-cell td-runs">
                  {#if item.calcState === "waiting"}
                    <div
                      class="cell-status-wrapper"
                      title="Queued to calculate"
                    >
                      <span class="triple-dot-pulse">
                        <span class="dot dot-1">•</span>
                        <span class="dot dot-2">•</span>
                        <span class="dot dot-3">•</span>
                      </span>
                    </div>
                  {:else if item.calcState === "calculating"}
                    <div class="cell-status-wrapper" title="Calculating runs">
                      <span class="spinner-wheel"></span>
                    </div>
                  {:else if item.calcState === "error" || item.calcState === "no-route"}
                    <span class="muted-dash">—</span>
                  {:else if item.runs !== null}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <span
                      class="runs-pill-count interactive-pill"
                      on:click|stopPropagation={() => openScheduleForYard(item.yard.id, true)}
                      title="Click to view turnaround route schedule"
                    >
                      {item.runs} runs
                    </span>
                    <div class="calc-sub-formula">
                      {item.tonnesDeliveredInDay}t / day
                    </div>
                  {:else}
                    <span class="muted-dash">—</span>
                  {/if}
                </div>

                <!-- Loads / Days Column -->
                <div class="td-cell td-loads-days">
                  {#if item.calcState === "waiting"}
                    <div
                      class="cell-status-wrapper"
                      title="Queued to calculate"
                    >
                      <span class="triple-dot-pulse">
                        <span class="dot dot-1">•</span>
                        <span class="dot dot-2">•</span>
                        <span class="dot dot-3">•</span>
                      </span>
                    </div>
                  {:else if item.calcState === "calculating"}
                    <div
                      class="cell-status-wrapper"
                      title="Calculating loads and days"
                    >
                      <span class="spinner-wheel"></span>
                    </div>
                  {:else if item.calcState === "error" || item.calcState === "no-route"}
                    <span class="muted-dash">—</span>
                  {:else if item.totalLoads !== null && item.totalDays !== null && item.totalLoads > 0}
                    <div class="loads-days-value">
                      <span class="loads-part">{item.totalLoads} loads</span>
                      <span class="divider-slash">/</span>
                      <span class="days-part">{formatDays(item.totalDays)}</span
                      >
                    </div>
                    <div class="calc-sub-formula">
                      {item.billedDays} billed {item.billedDays === 1
                        ? "day"
                        : "days"}
                    </div>
                  {:else if Number(tonnes) <= 0}
                    <span
                      class="no-tonnes-pill"
                      title="Enter Total Tonnes above to compute total loads and days"
                      >0 loads / 0 days</span
                    >
                  {:else}
                    <span class="muted-dash">—</span>
                  {/if}
                </div>

                <!-- Tolls Column -->
                <div class="td-cell td-tolls">
                  {#if item.calcState === "waiting"}
                    <div
                      class="cell-status-wrapper"
                      title="Queued to calculate"
                    >
                      <span class="triple-dot-pulse">
                        <span class="dot dot-1">•</span>
                        <span class="dot dot-2">•</span>
                        <span class="dot dot-3">•</span>
                      </span>
                    </div>
                  {:else if item.calcState === "calculating"}
                    <div
                      class="cell-status-wrapper"
                      title="Calculating route tolls"
                    >
                      <span class="spinner-wheel"></span>
                    </div>
                  {:else if item.calcState === "error" || item.calcState === "no-route"}
                    <span class="muted-dash">—</span>
                  {:else if item.totalTolls !== null && item.totalTolls > 0}
                    <div class="tolls-amount-value">
                      ${item.totalTolls.toLocaleString("en-AU", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <div
                      class="calc-sub-formula"
                      title="{item.dailyTolls ? '$' + item.dailyTolls.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '/day' : ''}{item.tollPerLoad ? ' • $' + item.tollPerLoad.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '/load' : ''}"
                    >
                      {#if item.dailyTolls && item.dailyTolls > 0}
                        ${Math.round(item.dailyTolls).toLocaleString("en-AU")}/day
                      {:else if item.tollPerLoad && item.tollPerLoad > 0}
                        ${Math.round(item.tollPerLoad).toLocaleString("en-AU")}/load
                      {/if}
                    </div>
                  {:else if item.calcState === "done"}
                    {@const tollErr = extractTollError(item)}
                    {#if tollErr}
                      <span
                        class="toll-error-pill"
                        title={tollErr}
                      >
                        ERROR
                      </span>
                      <div class="calc-sub-formula text-error" title={tollErr}>$0.00 tolls</div>
                    {:else if item.totalTolls !== null && item.totalTolls > 0}
                      <div class="tolls-amount-value">
                        ${item.totalTolls.toLocaleString("en-AU", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                      <div
                        class="calc-sub-formula"
                        title="{item.dailyTolls ? '$' + item.dailyTolls.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '/day' : ''}{item.tollPerLoad ? ' • $' + item.tollPerLoad.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '/load' : ''}"
                      >
                        {#if item.dailyTolls && item.dailyTolls > 0}
                          ${Math.round(item.dailyTolls).toLocaleString("en-AU")}/day
                        {:else if item.tollPerLoad && item.tollPerLoad > 0}
                          ${Math.round(item.tollPerLoad).toLocaleString("en-AU")}/load
                        {/if}
                      </div>
                    {:else}
                      <span
                        class="toll-free-pill"
                        title="No road tolls on this route"
                      >
                        No toll
                      </span>
                      <div class="calc-sub-formula">$0.00 tolls</div>
                    {/if}
                  {:else}
                    <span class="muted-dash">—</span>
                  {/if}
                </div>

                <!-- Total Cost Column -->
                <div class="td-cell td-total">
                  {#if item.calcState === "waiting"}
                    <div
                      class="cell-status-wrapper"
                      title="Queued to calculate"
                    >
                      <span class="triple-dot-pulse">
                        <span class="dot dot-1">•</span>
                        <span class="dot dot-2">•</span>
                        <span class="dot dot-3">•</span>
                      </span>
                    </div>
                  {:else if item.calcState === "calculating"}
                    <div
                      class="cell-status-wrapper"
                      title="Calculating total cost"
                    >
                      <span class="spinner-wheel"></span>
                    </div>
                  {:else if item.calcState === "error" || item.calcState === "no-route"}
                    <span class="muted-dash">—</span>
                  {:else if item.totalCost !== null}
                    <div class="total-cost-value">
                      ${item.totalCost.toLocaleString("en-AU", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    <div
                      class="calc-sub-formula"
                      title="{item.truckCost ? '$' + item.truckCost.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '$0.00'} truck + {item.materialCost ? '$' + item.materialCost.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '$0.00'} mat.{item.totalTolls && item.totalTolls > 0 ? ' + $' + item.totalTolls.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' toll' : ''}"
                    >
                      {formatCompactCost(item.truckCost)} trk + {formatCompactCost(item.materialCost)} mat{#if item.totalTolls && item.totalTolls > 0} + {formatCompactCost(item.totalTolls)} toll{/if}
                    </div>
                  {:else if item.hasRate}
                    <span
                      class="muted-dash"
                      title="Awaiting route to calculate runs">—</span
                    >
                  {:else}
                    <span class="muted-dash">—</span>
                  {/if}
                </div>

                <!-- Cost / Tonne Column -->
                <div class="td-cell td-cost-tonne">
                  {#if item.calcState === "waiting"}
                    <div
                      class="cell-status-wrapper"
                      title="Queued to calculate"
                    >
                      <span class="triple-dot-pulse">
                        <span class="dot dot-1">•</span>
                        <span class="dot dot-2">•</span>
                        <span class="dot dot-3">•</span>
                      </span>
                    </div>
                  {:else if item.calcState === "calculating"}
                    <div
                      class="cell-status-wrapper"
                      title="Calculating cost per tonne"
                    >
                      <span class="spinner-wheel"></span>
                    </div>
                  {:else if item.calcState === "error" || item.calcState === "no-route"}
                    <span class="muted-dash">—</span>
                  {:else if item.finalCostPerTonne !== null}
                    <div class="cost-tonne-value">
                      ${item.finalCostPerTonne.toLocaleString("en-AU", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}/t
                    </div>
                    <div class="calc-sub-formula">
                      total ÷ {Number(tonnes).toLocaleString()}t
                    </div>
                  {:else if Number(tonnes) <= 0}
                    <span
                      class="muted-dash"
                      title="Enter Total Tonnes above to compute cost per tonne"
                      >—</span
                    >
                  {:else}
                    <span class="muted-dash">—</span>
                  {/if}
                </div>

                <!-- Quote Action Column -->
                <div class="td-cell td-quote-action">
                  {#if item.calcState === "waiting" || item.calcState === "calculating"}
                    <span class="muted-dash">—</span>
                  {:else if item.totalCost !== null}
                    <button
                      class="add-quote-btn {isYardQuoted(item.yard.id, item.material)
                        ? 'is-active-quote'
                        : ''} {copiedYardId === item.yard.id
                        ? 'just-copied'
                        : ''}"
                      on:click|stopPropagation={() => copyToQuote(item)}
                      on:dblclick|stopPropagation={() => {}}
                      title="Copy this haulage package to Customer Quote"
                      type="button"
                    >
                      {#if copiedYardId === item.yard.id}
                        <span>✓ Copied</span>
                      {:else if isYardQuoted(item.yard.id, item.material)}
                        <span>✓ In Quote</span>
                      {:else}
                        <span>+ Quote</span>
                      {/if}
                    </button>
                  {:else}
                    <span class="muted-dash">—</span>
                  {/if}
                </div>
              </div>
            {/each}
          </div>
        </div>
      </div>
    {/if}
  </div>
</div>

{#if showYardDetails && selectedYard}
  <YardDeatils
    yardId={selectedYard.id}
    bind:yard={selectedYard}
    on:close={onYardDetailsClose}
    on:yard-saved={(e) => handleYardSaved(e.detail)}
    on:saved={(e) => handleYardSaved(e.detail)}
    on:edit-company={setShowYardCompany}
  />
{/if}

{#if showYardCompany}
  <CompanyForm
    bind:company={yardCompany}
    on:close={() => (showYardCompany = false)}
    on:company-closed={() => (showYardCompany = false)}
    on:company-saved={saveCompanyDetails}
  />
{/if}

<style>
  .calc-window {
    position: absolute;
    top: 4rem;
    left: 1rem;
    width: 600px;
    max-width: calc(100vw - 2rem);
    max-height: calc(100% - 5rem);
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(16px);
    border: 1px solid var(--tt-slate-200);
    border-radius: var(--tt-radius-lg);
    box-shadow: var(--tt-shadow-xl);
    z-index: 1200;
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: fadeIn 0.2s ease-out;
    transition: width 0.25s ease;
  }

  .calc-window.has-results {
    width: 920px;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 1.125rem;
    border-bottom: 1px solid var(--tt-slate-200);
    background-color: var(--tt-slate-50);
  }

  .header-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .header-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .card-title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--tt-slate-900);
    letter-spacing: -0.01em;
  }

  .card-subtitle {
    font-size: 0.75rem;
    color: var(--tt-slate-500);
  }

  .close-card-btn {
    width: 32px;
    height: 32px;
    border-radius: var(--tt-radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    color: var(--tt-slate-500);
    cursor: pointer;
    transition: all 0.15s ease;
    margin: 0;
    padding: 0;
  }

  .close-card-btn:hover {
    background: var(--tt-slate-200);
    color: var(--tt-slate-800);
  }

  .card-body {
    padding: 1rem 1.125rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .calc-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border-radius: var(--tt-radius-sm);
    font-size: 12px;
    line-height: 1.4;
  }

  .calc-banner.notice {
    background-color: var(--tt-slate-100);
    border: 1px solid var(--tt-slate-300);
    color: var(--tt-slate-700);
  }

  .calc-banner.success {
    background-color: var(--tt-success-bg);
    border: 1px solid var(--tt-success-border);
    color: var(--tt-success-text);
  }

  .banner-icon {
    font-size: 14px;
    line-height: 1;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .banner-content strong {
    font-weight: 600;
  }

  .calc-section-title {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-500);
    margin-top: 2px;
  }

  .calc-grid-5 {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 8px;
    align-items: end;
  }

  .calc-fleet-grid {
    display: grid;
    grid-template-columns: 1.7fr 0.9fr 1.7fr 1.2fr 1.5fr;
    gap: 8px;
    align-items: end;
  }

  @media (max-width: 560px) {
    .calc-grid-5 {
      grid-template-columns: repeat(2, 1fr);
    }
    .calc-fleet-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .calc-field {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .calc-window :global(.bx--form-item) {
    margin: 0 !important;
    width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: flex-end !important;
  }

  .calc-window :global(.bx--list-box__wrapper) {
    width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: flex-end !important;
  }

  .calc-window :global(.bx--label) {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    font-size: 11px !important;
    line-height: 14px !important;
    height: 14px !important;
    margin-bottom: 4px !important;
    display: block !important;
  }

  .calc-window :global(.bx--text-input--sm) {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
    font-size: 13px !important;
  }

  .calc-window :global(.bx--list-box--sm) {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
  }

  .calc-window :global(.bx--list-box--sm .bx--list-box__field) {
    height: 32px !important;
    min-height: 32px !important;
    max-height: 32px !important;
  }

  .calc-window :global(.bx--list-box--sm .bx--list-box__label) {
    font-size: 12px !important;
    line-height: 30px !important;
  }

  .calc-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
    padding-top: 10px;
    border-top: 1px solid var(--tt-slate-200);
  }

  .back-planner-btn {
    background: transparent;
    border: none;
    color: var(--tt-primary);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 6px 4px;
    transition: all 0.15s ease;
  }

  .back-planner-btn:hover {
    color: var(--tt-primary-hover);
    text-decoration: underline;
  }

  :global(.calc-actions .bx--btn) {
    border-radius: var(--tt-radius-sm);
    padding-left: 20px;
    padding-right: 20px;
  }

  .tt-badge-slate {
    background-color: var(--tt-slate-100);
    color: var(--tt-slate-600);
    border: 1px solid var(--tt-slate-300);
  }

  .tt-badge-estimate {
    background-color: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .results-estimate-chip {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #1d4ed8;
    background: #dbeafe;
    border: 1px solid #bfdbfe;
    padding: 1px 6px;
    border-radius: 4px;
  }

  .results-expansion-panel {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 6px;
    padding-top: 10px;
    border-top: 1px solid var(--tt-slate-200);
    animation: slideDown 0.22s ease-out;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .results-header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
  }

  .results-title-group {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .results-title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .results-main-title {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-700);
  }

  .results-count-pill {
    font-size: 11px;
    font-weight: 600;
    color: var(--tt-primary);
    background: rgba(66, 133, 244, 0.08);
    padding: 1px 7px;
    border-radius: 9999px;
  }

  .results-formula-hint {
    font-size: 10.5px;
    color: var(--tt-slate-500);
    line-height: 1.3;
  }

  .cheapest-summary-badge {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    color: #047857;
    padding: 3px 8px;
    border-radius: 9999px;
    font-size: 11px;
  }

  .summary-star {
    color: #059669;
    font-size: 11px;
  }

  .summary-text strong {
    font-weight: 700;
  }

  .results-table-container {
    border: 1px solid var(--tt-slate-200);
    border-radius: var(--tt-radius-md);
    background: #ffffff;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .results-table-head {
    display: flex;
    align-items: center;
    background-color: var(--tt-slate-50);
    border-bottom: 1px solid var(--tt-slate-200);
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--tt-slate-500);
    user-select: none;
  }

  .results-table-body {
    max-height: 280px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    scrollbar-width: thin;
    scrollbar-color: var(--tt-slate-300) transparent;
  }

  .results-table-body::-webkit-scrollbar {
    width: 5px;
  }

  .results-table-body::-webkit-scrollbar-thumb {
    background: var(--tt-slate-300);
    border-radius: 4px;
  }

  .result-row {
    display: flex;
    align-items: center;
    padding: 6px 10px;
    border-bottom: 1px solid var(--tt-slate-100);
    transition: background-color 0.12s ease;
    cursor: pointer;
    user-select: none;
  }

  .result-row:last-child {
    border-bottom: none;
  }

  .result-row:hover {
    background-color: #f8fbff;
  }

  .result-row.cheapest-row {
    background-color: #f0fdf4;
    border-left: 3px solid #10b981;
  }

  .result-row.cheapest-row:hover {
    background-color: #e6fbf0;
  }

  .result-row.unpriced-row {
    background-color: #fafafa;
    opacity: 0.85;
  }

  .result-row.unpriced-row:hover {
    opacity: 1;
    background-color: #f4f4f5;
  }

  .th-cell,
  .td-cell {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .th-yard,
  .td-yard {
    flex: 1.35;
    min-width: 140px;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  .th-cost-load,
  .td-cost-load {
    flex: 0.95;
    min-width: 90px;
    text-align: right;
    align-items: flex-end;
  }

  .th-runs,
  .td-runs {
    flex: 0.75;
    min-width: 70px;
    text-align: center;
    align-items: center;
  }

  .th-loads-days,
  .td-loads-days {
    flex: 1.05;
    min-width: 100px;
    text-align: center;
    align-items: center;
    justify-content: center;
  }

  .th-tolls,
  .td-tolls {
    flex: 0.85;
    min-width: 80px;
    text-align: right;
    align-items: flex-end;
    padding-right: 6px;
  }

  .th-total,
  .td-total {
    flex: 1.35;
    min-width: 135px;
    text-align: right;
    align-items: flex-end;
    padding-left: 6px;
  }

  .th-cost-tonne,
  .td-cost-tonne {
    flex: 1.05;
    min-width: 100px;
    text-align: right;
    align-items: flex-end;
  }

  .th-quote-action,
  .td-quote-action {
    flex: 0.75;
    min-width: 70px;
    text-align: center;
    align-items: center;
    justify-content: center;
  }

  .loads-days-value {
    font-size: 11.5px;
    font-weight: 700;
    color: var(--tt-slate-800);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    white-space: nowrap;
  }

  .loads-days-value .loads-part {
    color: var(--tt-slate-900);
  }

  .loads-days-value .divider-slash {
    color: var(--tt-slate-400);
    font-weight: 400;
    margin: 0 1px;
  }

  .loads-days-value .days-part {
    color: var(--tt-primary);
  }

  .cheapest-row .loads-days-value .days-part {
    color: #047857;
  }

  .no-tonnes-pill {
    font-size: 10px;
    color: var(--tt-slate-400);
    background: var(--tt-slate-100);
    padding: 1px 5px;
    border-radius: 3px;
    white-space: nowrap;
  }

  .tolls-amount-value {
    font-size: 13px;
    font-weight: 700;
    color: var(--tt-slate-800);
    line-height: 1.2;
  }

  .toll-free-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 7px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    color: #047857;
    background-color: #d1fae5;
    border: 1px solid #a7f3d0;
    line-height: 1.2;
    white-space: nowrap;
  }

  .toll-error-pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 7px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    color: #b91c1c;
    background-color: #fee2e2;
    border: 1px solid #fca5a5;
    line-height: 1.2;
    white-space: nowrap;
    cursor: help;
    letter-spacing: 0.03em;
    transition: all 0.15s ease;
  }

  .toll-error-pill:hover {
    background-color: #fecaca;
    border-color: #f87171;
    box-shadow: 0 1px 3px rgba(185, 28, 28, 0.18);
  }

  .calc-sub-formula.text-error {
    color: #dc2626;
    cursor: help;
  }

  .add-quote-btn {
    background: #ffffff;
    color: var(--tt-primary);
    border: 1px solid var(--tt-primary-border);
    padding: 3px 8px;
    border-radius: var(--tt-radius-sm);
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.12s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
  }

  .add-quote-btn:hover {
    background: var(--tt-primary);
    color: #ffffff;
    box-shadow: 0 1px 4px rgba(66, 133, 244, 0.25);
  }

  .add-quote-btn.is-active-quote {
    background: #ecfdf5;
    color: #047857;
    border-color: #a7f3d0;
  }

  .add-quote-btn.just-copied {
    background: #10b981;
    color: #ffffff;
    border-color: #10b981;
  }

  .yard-thumb-wrapper {
    width: 26px;
    height: 26px;
    border-radius: 4px;
    border: 1px solid var(--tt-slate-200);
    overflow: hidden;
    background: var(--tt-slate-100);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .yard-thumb {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder-avatar {
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 26px;
  }

  .yard-info-col {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .yard-name-row {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .yard-name-text {
    font-size: 12px;
    font-weight: 700;
    color: var(--tt-slate-900);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 180px;
  }

  .yard-row-edit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 3px;
    border: none;
    background: transparent;
    color: var(--tt-slate-400);
    cursor: pointer;
    padding: 0;
    opacity: 0;
    transition:
      opacity 0.12s ease,
      color 0.12s ease,
      background-color 0.12s ease;
  }

  .result-row:hover .yard-row-edit-btn {
    opacity: 1;
  }

  .yard-row-edit-btn:hover {
    background-color: var(--tt-slate-200);
    color: var(--tt-primary);
  }

  .yard-suburb-text {
    font-size: 10.5px;
    color: var(--tt-slate-500);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 170px;
  }

  .best-badge {
    font-size: 10px;
    font-weight: 700;
    color: #047857;
    background-color: #d1fae5;
    border: 1px solid #a7f3d0;
    padding: 0 5px;
    border-radius: 4px;
    white-space: nowrap;
  }

  .cost-load-value {
    font-size: 12px;
    font-weight: 700;
    color: var(--tt-slate-800);
  }

  .calc-sub-formula {
    font-size: 10px;
    color: var(--tt-slate-400);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .runs-pill-count {
    font-size: 11px;
    font-weight: 700;
    color: var(--tt-slate-800);
    background: var(--tt-slate-100);
    border: 1px solid var(--tt-slate-200);
    padding: 1px 6px;
    border-radius: 4px;
    white-space: nowrap;
  }

  .runs-pill-count.interactive-pill {
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .runs-pill-count.interactive-pill:hover {
    background: #dbeafe;
    color: #1d4ed8;
    border-color: #93c5fd;
    box-shadow: 0 1px 4px rgba(37, 99, 235, 0.2);
  }

  .total-cost-value {
    font-size: 12.5px;
    font-weight: 800;
    color: var(--tt-slate-900);
  }

  .cost-tonne-value {
    font-size: 12px;
    font-weight: 800;
    color: var(--tt-slate-900);
  }

  .cheapest-row .total-cost-value {
    color: #047857;
  }

  .cheapest-row .cost-load-value {
    color: #047857;
    font-weight: 800;
  }

  .cheapest-row .cost-tonne-value {
    color: #047857;
    font-weight: 800;
  }

  .no-rate-pill {
    font-size: 10.5px;
    color: var(--tt-slate-400);
    background: var(--tt-slate-100);
    padding: 1px 6px;
    border-radius: 3px;
    white-space: nowrap;
  }

  .muted-dash {
    font-size: 13px;
    color: var(--tt-slate-300);
    font-weight: 600;
  }

  @media (max-width: 640px) {
    .calc-window.has-results {
      width: calc(100vw - 2rem);
    }

    .results-table-head,
    .result-row {
      padding: 6px 8px;
    }

    .th-yard,
    .td-yard {
      min-width: 130px;
    }

    .yard-name-text {
      max-width: 110px;
    }
  }

  .btn-spinner {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spinWheel 0.75s linear infinite;
    margin-right: 6px;
    vertical-align: middle;
  }

  .spinner-wheel {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(59, 130, 246, 0.25);
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: spinWheel 0.75s linear infinite;
    flex-shrink: 0;
  }

  .spinner-wheel.small {
    width: 10px;
    height: 10px;
    border-width: 1.5px;
  }

  @keyframes spinWheel {
    to {
      transform: rotate(360deg);
    }
  }

  .triple-dot-pulse {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 14px;
    line-height: 1;
    color: var(--tt-slate-400);
    user-select: none;
  }

  .triple-dot-pulse.small {
    gap: 2px;
    font-size: 10px;
  }

  .triple-dot-pulse .dot {
    display: inline-block;
    animation: dotPulse 1.4s infinite ease-in-out both;
  }

  .triple-dot-pulse .dot-1 {
    animation-delay: -0.32s;
  }

  .triple-dot-pulse .dot-2 {
    animation-delay: -0.16s;
  }

  .triple-dot-pulse .dot-3 {
    animation-delay: 0s;
  }

  @keyframes dotPulse {
    0%,
    80%,
    100% {
      transform: scale(0.65);
      opacity: 0.35;
      color: var(--tt-slate-400);
    }
    40% {
      transform: scale(1.2);
      opacity: 1;
      color: #3b82f6;
    }
  }

  .calc-status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 600;
    padding: 1px 5px;
    border-radius: 4px;
    white-space: nowrap;
    line-height: 1.2;
  }

  .calc-status-badge.badge-waiting {
    background: var(--tt-slate-100);
    color: var(--tt-slate-600);
    border: 1px solid var(--tt-slate-200);
  }

  .calc-status-badge.badge-calculating {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
    animation: pulseSubtle 1.5s infinite;
  }

  .calc-status-badge.badge-error {
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
  }

  .calc-status-badge.badge-no-route {
    background: var(--tt-slate-100);
    color: var(--tt-slate-400);
    border: 1px solid var(--tt-slate-200);
  }

  @keyframes pulseSubtle {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }

  .result-row.is-calculating {
    background: linear-gradient(
      90deg,
      rgba(239, 246, 255, 0.95),
      rgba(248, 250, 252, 0.9)
    );
    border-left: 3px solid #3b82f6 !important;
    box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.2) inset;
  }

  .cell-status-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 5px;
    min-height: 20px;
  }

  .td-runs .cell-status-wrapper,
  .td-loads-days .cell-status-wrapper,
  .td-total .cell-status-wrapper {
    justify-content: center;
  }

  .cell-status-wrapper.is-active-calc {
    color: #2563eb;
    font-size: 11px;
    font-weight: 600;
  }

  .calc-inline-label {
    font-size: 11px;
    color: #2563eb;
    font-weight: 600;
    white-space: nowrap;
  }

  .results-calc-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 10.5px;
    font-weight: 600;
    color: #1d4ed8;
    background: #dbeafe;
    border: 1px solid #bfdbfe;
    padding: 1px 7px;
    border-radius: 4px;
  }

  .tt-badge-calculating {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background-color: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .no-rate-pill.error-pill {
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
  }

</style>
